/**
 * Coordinated hard-reload for post-deploy chunk-load failures (XT-413).
 *
 * Prod lesson (2026-07-16): clearing the "attempted" flag on every shell boot
 * caused a reload loop during mid-deploy asset gaps — boot succeeded, flag
 * reset, lazy route failed again, auto-reload again, until /client-events
 * rate-limited the IP.
 *
 * Rules now:
 * 1. Chunk fail → if outside cooldown → stamp time + hard reload once.
 * 2. Shell boot → strip `_app_reload` only; **do not** clear the cooldown.
 * 3. Chunk fail inside cooldown → show "Reload app" UI (manual only).
 * 4. After `AUTO_RELOAD_COOLDOWN_MS` the stamp expires so a *later* deploy
 *    can auto-recover again without a full browser session restart.
 */

export const CHUNK_RELOAD_FLAG_KEY = 'tp:chunk-reload-attempted-at';
export const APP_RELOAD_QUERY_PARAM = '_app_reload';

/** Min gap between automatic hard-reloads (per tab sessionStorage). */
export const AUTO_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

export const hasChunkReloadBeenAttempted = (
  now: number = Date.now()
): boolean => {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_FLAG_KEY);
    if (!raw) return false;
    // Legacy boolean flag from the first ship (`"1"`) — treat as active.
    if (raw === '1') return true;
    const at = Number(raw);
    if (!Number.isFinite(at)) return true;
    return now - at < AUTO_RELOAD_COOLDOWN_MS;
  } catch {
    // Private mode / blocked storage — treat as "already tried" so we
    // never auto-loop when we cannot remember the attempt.
    return true;
  }
};

export const markChunkReloadAttempted = (now: number = Date.now()): void => {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_FLAG_KEY, String(now));
  } catch {
    // ignore
  }
};

export const clearChunkReloadAttempt = (): void => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG_KEY);
  } catch {
    // ignore
  }
};

/**
 * Full document navigation with a cache-bust query. Prefer `replace` so
 * Back does not re-enter the broken shell.
 */
export const hardReloadApp = (): void => {
  const url = new URL(window.location.href);
  url.searchParams.set(APP_RELOAD_QUERY_PARAM, String(Date.now()));
  window.location.replace(url.toString());
};

/**
 * Drop the bust param after a successful boot so shared URLs stay clean.
 * Uses `history.replaceState` (no navigation).
 */
export const stripAppReloadQueryParam = (): void => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(APP_RELOAD_QUERY_PARAM)) return;
  url.searchParams.delete(APP_RELOAD_QUERY_PARAM);
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
};

/**
 * Call once when the app shell mounts. Only cleans the URL — never clears
 * the auto-reload cooldown (that would re-enable loops during bad deploys).
 */
export const onAppBootSuccess = (): void => {
  stripAppReloadQueryParam();
};
